import { call, put, select } from 'redux-saga/effects';

import * as actions from './meetings-simple.actions';

import * as scoreApi from '../../../api/request-config/score.api';
import * as meetingApi from '../../../api/request-config/meeting.api';
import * as notificationApi from '../../../api/request-config/notification.api';
import * as sessionResourceService from '../../../resource/session/session.saga-service';
import * as dialogService from '../../../dialog/dialog.saga-services';
import * as meetingSimpleResourceService from '../../../resource/meeting-simple/meeting-simple.saga-service';
import { meetingSimpleSelectors } from '../../../resource/meeting-simple/meeting-simple.selectors';
import {
  IMeetingSimple,
  IMeetingSimpleCreate,
  IMeetingSimpleCreateForm,
  IMeetingSimpleResponse,
  IMeetingSimpleUpdate,
  IStartMeetingResponse,
} from '../../../resource/meeting-simple/meeting-simple.types';
import { meetingsSimplePageSelectors } from './meetings-simple.selectors';

import { formSelectors } from '../users/user-form/user-form.selectors';
import { selectWithParams } from '../../../../utils/selector.utils';
import { clearFormAvatar, saveCropCounter, saveFormAvatar } from '../users/user-form/user-form.actions';
import { IUser } from '../../../resource/user/user.types';
import { ISession } from '../../../resource/session/session.types';
import { sessionSelectors } from '../../../resource/session/session.selectors';
import { IScore } from '../../../resource/score/score.types';
import { IKpiRowScoreboard } from '../../../resource/kpi-row-scoreboard/kpi-row-scoreboard.types';
import { storeSessionIds as kpiPageSessionIds, storeIsFiltered } from '../kpi/kpi.actions';
import { apiRequest } from '../../../api/api.saga-services';
import { IMeetingParticipantsUpdateForm } from '../../../resource/meeting/meeting.types';
import { authSelectors } from '../../../auth/auth.selectors';
import { addFlashMessage } from '../../../../utils/flashMessages.utils';
import { findNextMeeting } from '../../../../utils/meeting.utils';
import { storeSessionIdsForPage } from '../sessions/sessions.saga-services';
import { push } from 'connected-react-router';
import { IKpi } from '../../../resource/kpi/kpi.types';
import { kpiSelectors } from '../../../resource/kpi/kpi.selectors';
import { IMeetingFolderUpdateForm } from '../../../resource/meeting-folder/meeting-folder.types';
import { fetchKpi } from '../kpi/kpi.saga-services';
import { kpiPageSelectors } from '../kpi/kpi.selectors';
import * as kpiService from '../kpi/kpi.saga-services';

export function* fetchLiveSessions() {
  try {
    const liveSessions = (yield call(apiRequest, meetingApi.fetchLiveSessions())) as ISession[];
    yield put(actions.saveLiveSessions(liveSessions));
  } catch (error) {
    console.error('Error while fetching live sessions:', error);
  }
}

export function* clearMeetingsSimple() {
  yield put(actions.clearMeetingSimple());
}

export function* setupMeetingsSimple() {
  const isFetched = (yield select(meetingsSimplePageSelectors.selectIsFetched)) as boolean;
  let meetingsSimple: IMeetingSimple[] = [];

  if (!isFetched) {
    meetingsSimple = (yield call(meetingSimpleResourceService.searchMeetingsSimple)) as IMeetingSimple[];
    meetingsSimple = meetingsSimple.sort((a, b) => a.title.localeCompare(b.title));
    yield put(meetingsSimple ? actions.storeIsFetched() : actions.clearIsFetched());
  } else {
    meetingsSimple = yield select(meetingSimpleSelectors.selectResourceList);
  }
  yield put(actions.storeMeetingsSimple(meetingsSimple.map((meetingSimple) => meetingSimple.id)));

  const accountIds = meetingsSimple.reduce(
    (prev: IMeetingSimple['id'][], curr) => (curr.type === 'account' ? [...prev, curr.id] : prev),
    [],
  );

  const teamIds = meetingsSimple.reduce(
    (prev: IMeetingSimple['id'][], curr) => (curr.type === 'meeting' ? [...prev, curr.id] : prev),
    [],
  );

  const nextMeeting = findNextMeeting(meetingsSimple);

  yield put(actions.storeAccounts(accountIds));
  yield put(actions.storeTeams(teamIds));
  if (nextMeeting) {
    yield put(actions.storeNextMeeting(nextMeeting.id));
  }
}

export function* createMeetingSimple(meeting: IMeetingSimpleCreateForm) {
  const createdMeeting = (yield call(meetingSimpleResourceService.createMeetingSimple, {
    ...meeting,
    repeatEvery: meeting.repeatEvery === 0 ? '1' : meeting.repeatEvery,
    repeatType: meeting.repeatType ? meeting.repeatType : null,
    startTime: meeting.startTime,
    administrators: meeting.administrators,
  } as IMeetingSimpleCreate)) as IMeetingSimpleResponse;

  if (!createdMeeting) {
    return;
  }
  if (createdMeeting.type === 'account') {
    const accountIds: IMeetingSimple['id'][] = yield select(meetingsSimplePageSelectors.selectAccounts);
    accountIds.push(createdMeeting.id);
    yield put(actions.storeAccounts(accountIds));
  }
  if (createdMeeting.type === 'team') {
    const teamIds: IMeetingSimple['id'][] = yield select(meetingsSimplePageSelectors.selectTeams);
    teamIds.push(createdMeeting.id);
    yield put(actions.storeTeams(teamIds));
  }
  if (createdMeeting.type === 'meeting') {
    const meetingIds: IMeetingSimple['id'][] = yield select(meetingsSimplePageSelectors.selectMeetingsSimple);
    meetingIds.push(createdMeeting.id);
    yield put(actions.storeMeetingsSimple(meetingIds));

    const teamIds: IMeetingSimple['id'][] = yield select(meetingsSimplePageSelectors.selectTeams);
    teamIds.push(createdMeeting.id);
    yield put(actions.storeTeams(teamIds));
  }
  const nextMeetingId: string = yield select(meetingsSimplePageSelectors.selectNextMeeting);
  const nextMeeting: IMeetingSimple = yield selectWithParams(meetingSimpleSelectors.selectResourceById, nextMeetingId);

  if (
    (createdMeeting.nextSession &&
      nextMeeting?.nextSession &&
      new Date(createdMeeting.nextSession) <= new Date(nextMeeting.nextSession)) ||
    !nextMeeting?.nextSession
  ) {
    yield put(actions.storeNextMeeting(createdMeeting.id));
  }

  yield put(clearFormAvatar());
  yield call(dialogService.closeDialog, 'meetingTemplatesFormDialog');
}

export function* updateMeetingParticipants(id: IMeetingSimple['id'], data: IMeetingParticipantsUpdateForm) {
  yield call(meetingSimpleResourceService.updateMeetingParticipants, id, data);
}

export function* updateFolder(id: IMeetingSimple['id'], data: IMeetingFolderUpdateForm) {
  yield call(meetingSimpleResourceService.updateMeetingFolder, id, data);
}

export function* updateMeetingSimple(id: IMeetingSimple['id'], data: IMeetingSimpleUpdate) {
  const imageData = (yield select(formSelectors.selectFormAvatar)) as string;
  const cropCounter = (yield select(formSelectors.selectCropCounter)) as number;

  const updatedMeeting = (yield call(meetingSimpleResourceService.updateMeetingSimple as any, id, {
    ...data,
    image: cropCounter > 1 ? imageData : undefined,
  })) as IMeetingSimple;

  if (!updatedMeeting) {
    return;
  }

  yield call(kpiService.setupKpi, id);
  yield call(addFlashMessage, { type: 'success', message: 'The Meeting was successfully updated.' });

  yield call(dialogService.closeDialog, 'meetingFormDialog');
  yield put(saveFormAvatar(''));
  yield put(saveCropCounter(0));
  yield put(actions.updateMeetingSimpleComplete());
}

export function* saveMeetingSimpleSocket(meeting: IMeetingSimpleResponse) {
  const loggedUserId = (yield select(authSelectors.selectLoggedUser)) as IUser['id'];
  if (String(meeting.actionUserId) == loggedUserId) {
    return;
  }
  const meetingsSimple: IMeetingSimple['id'][] = yield select(meetingsSimplePageSelectors.selectMeetingsSimple);
  if (meetingsSimple.includes(meeting.id)) {
    const updatedMeeting: IMeetingSimple = yield call(meetingSimpleResourceService.meetingResponseToMeeting, meeting);
    yield call(meetingSimpleResourceService.storeMeetingSimple, updatedMeeting);
  } else {
    const newMeeting: IMeetingSimple = yield call(meetingSimpleResourceService.meetingResponseToMeeting, meeting);
    const meetingIds: IMeetingSimple['id'][] = yield select(meetingsSimplePageSelectors.selectMeetingsSimple);
    const newMeetingIds = [...meetingIds];
    newMeetingIds.push(newMeeting.id);
    yield call(meetingSimpleResourceService.storeMeetingSimple, newMeeting);
    yield put(actions.storeMeetingsSimple(newMeetingIds));
    yield put(actions.storeTeams(newMeetingIds));
  }
}

export function* deleteMeetingSimple(meetingId: IMeetingSimple['id']) {
  const res = yield call(meetingSimpleResourceService.deleteMeetingSimple, meetingId);
  if (res) {
    yield call(removeMeetingFromStore, meetingId);
  }
}

export function* deleteMeetingSimpleSocket({ id, actionUserId }: { id: IMeetingSimple['id']; actionUserId: number }) {
  const loggedUserId = (yield select(authSelectors.selectLoggedUser)) as IUser['id'];
  if (String(actionUserId) == loggedUserId) {
    return;
  }
  yield call(removeMeetingFromStore, id);
}

function* removeMeetingFromStore(meetingId: IMeetingSimple['id']) {
  const accountIds: IMeetingSimple['id'][] = yield select(meetingsSimplePageSelectors.selectAccounts);
  const newAccIds = accountIds.filter((id) => id !== meetingId);
  yield put(actions.storeAccounts(newAccIds));
  const teamIds: IMeetingSimple['id'][] = yield select(meetingsSimplePageSelectors.selectTeams);
  const newTeamIds = teamIds.filter((id) => id !== meetingId);
  yield put(actions.storeTeams(newTeamIds));
  const meetingIds: IMeetingSimple['id'][] = yield select(meetingsSimplePageSelectors.selectMeetingsSimple);
  const newMeetingIds = meetingIds.filter((id) => id !== meetingId);
  yield put(actions.storeMeetingsSimple(newMeetingIds));

  yield call(meetingSimpleResourceService.clearMeetingSimple, meetingId);
}

export function* startMeetingSimpleSession(meetingId: IMeetingSimple['id']) {
  const { updatedSessionsIds, startedSessionId } = yield call(
    meetingSimpleResourceService.startMeetingSession,
    meetingId,
  );

  // this call makes the live sidebar display the started meeting, replaceable by updating the store manually
  yield call(fetchLiveSessions);

  // this fires if kpis were filtered by user in meeting dashboard page before starting the session
  // we do not want filtered kpis during the live session
  yield call(fetchAllKpisIfAreFiltered, meetingId);

  yield call(storeSessionIdsForPage, updatedSessionsIds);
  yield put(push(`/live-meeting/${meetingId}/${startedSessionId}`));
}

export function* startMeetingSessionSocket(meeting: IStartMeetingResponse) {
  const loggedUserId = (yield select(authSelectors.selectLoggedUser)) as IUser['id'];
  if (String(meeting.actionUserId) == loggedUserId) {
    return;
  }

  yield call(fetchLiveSessions);
  const startedMeetings = (yield select(meetingsSimplePageSelectors.selectStartedMeetings)) as IMeetingSimple['id'][];
  if (startedMeetings.includes(meeting.id)) {
    return;
  }

  const kpi = (yield selectWithParams(kpiSelectors.selectResourceById, meeting.id)) as IKpi;
  if (!kpi) {
    return;
  }

  yield call(meetingSimpleResourceService.prepareStartedSessionInStore, meeting.session, meeting.id);

  const newStartedMeetings = [...startedMeetings, meeting.id];
  yield put(actions.storeStartedMeetingsSimple(newStartedMeetings));
}

export function* joinMeeting(session: ISession) {
  yield call(meetingSimpleResourceService.populateKpi, session.meetingId);

  // this fires if kpis were filtered by user in meeting dashboard page before starting the session
  // we do not want filtered kpis during the live session
  yield call(fetchAllKpisIfAreFiltered, session.meetingId);

  yield call(meetingSimpleResourceService.joinMeeting, session.id);
  yield put(push(`/live-meeting/${session.meetingId}/${session.id}`));
}

function* fetchAllKpisIfAreFiltered(meetingId: IMeetingSimple['id']) {
  const areKpisFiltered = (yield select(kpiPageSelectors.selectIsFiltered)) as boolean;
  if (areKpisFiltered) {
    yield call(fetchKpi, meetingId);
    yield put(storeIsFiltered(false));
  }
}

export function* joinMeetingSocket(actionUserId: number, sessionId: ISession['id'], startedBy: ISession['startedBy']) {
  // if it was me who joined the session, then there's no need to do anything
  const loggedUserId = (yield select(authSelectors.selectLoggedUser)) as IUser['id'];

  if (String(actionUserId) == loggedUserId) {
    return;
  }
  yield call(fetchLiveSessions);
  const session = (yield selectWithParams(sessionSelectors.selectResourceById, sessionId)) as ISession;

  // if user has not entered to started meeting dashboard yet, sessions are not loaded.
  if (session) {
    const updatedMembers = (yield call(
      meetingSimpleResourceService.addUserToMeetingMembers,
      String(actionUserId),
      session.meetingMembers,
    )) as IUser['id'][];
    yield call(sessionResourceService.storeUpdatedSessionResource, session, {
      startedBy,
      meetingMembers: updatedMembers,
    });
  }
}

export function* finishMeetingSimpleSessionSocket(meetingId: IMeetingSimple['id'], sessionId: ISession['id']) {
  yield call(fetchLiveSessions);
  const startedMeetings = (yield select(meetingsSimplePageSelectors.selectStartedMeetings)) as IMeetingSimple['id'][];
  const newStartedMeetings = startedMeetings.filter((startedId) => startedId !== meetingId);
  yield put(actions.storeStartedMeetingsSimple(newStartedMeetings));
  const { sessions } = (yield call(apiRequest, scoreApi.getScoreBoardApi({ meetingId }))) as {
    scores: IScore[];
    sessions: ISession[];
    scoreboards: {
      [sessionId: string]: IKpiRowScoreboard[];
    };
  };
  const sessionsIds = sessions.map((session) => session.id);
  yield put(kpiPageSessionIds(sessionsIds));
  const session = (yield selectWithParams(sessionSelectors.selectResourceById, sessionId)) as ISession;
  yield call(sessionResourceService.storeSession, { ...session, currentStep: '0', lastStepStartedAt: '' });
}

export function* finishMeetingSimpleSession(
  meetingId: IMeetingSimple['id'],
  sessionId: ISession['id'],
  cancelled: boolean,
) {
  const startedMeetings = (yield select(meetingsSimplePageSelectors.selectStartedMeetings)) as IMeetingSimple['id'][];
  const newStartedMeetings = startedMeetings.filter((startedId) => startedId !== meetingId);
  yield put(actions.storeStartedMeetingsSimple(newStartedMeetings));
  yield call(fetchLiveSessions);
  const { sessions } = (yield call(apiRequest, scoreApi.getScoreBoardApi({ meetingId }))) as {
    scores: IScore[];
    sessions: ISession[];
    scoreboards: {
      [sessionId: string]: IKpiRowScoreboard[];
    };
  };
  const sessionsIds = sessions.map((session) => session.id);
  yield put(kpiPageSessionIds(sessionsIds));
  const session = (yield selectWithParams(sessionSelectors.selectResourceById, sessionId)) as ISession;
  yield call(sessionResourceService.storeSession, { ...session, currentStep: '0', lastStepStartedAt: '' });
  yield call(meetingSimpleResourceService.finishMeetingSession, meetingId, sessionId, cancelled);
}

export function* clearMeetingsSimplePageStorage() {
  yield put(actions.clearMeetingSimple());
  yield put(actions.clearIsFetched());
}

export function* sendMessageToMeetingMembers(
  sender_id: IUser['id'],
  receiver_ids: IUser['id'][],
  meeting_id: IMeetingSimple['id'],
  message: string,
) {
  const response = yield call(
    apiRequest,
    notificationApi.sendMessageToMeetingMembersApi({
      sender_id,
      receiver_ids,
      meeting_id,
      message,
      type: 'team',
    }),
  );
  if (!response) {
    return;
  }

  yield call(addFlashMessage, {
    type: 'success',
    message: 'Message sent successfully to meeting members',
  });
}
